import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ActiveIncident,
	type BlozIncident,
	createEventRule,
	type DispatchResult,
	deleteEventRule,
	deleteEventRuleImage,
	type EventRule,
	listBlozIncidents,
	listDeviceEventIncidents,
	listEventRules,
	type RuleInput,
	setEventRuleImage,
	testEventRuleCanary,
	updateEventRule,
} from "./event-rules-api";

export const EVENT_RULES_QUERY_KEY = ["device-agent", "event-rules"] as const;

export function useEventRulesQuery() {
	return useQuery<EventRule[]>({
		queryKey: EVENT_RULES_QUERY_KEY,
		queryFn: listEventRules,
	});
}

export function useCreateEventRule() {
	const queryClient = useQueryClient();
	return useMutation<EventRule, Error, RuleInput>({
		mutationFn: createEventRule,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: EVENT_RULES_QUERY_KEY });
		},
	});
}

export interface UpdateRuleArgs {
	ruleId: number;
	input: RuleInput;
}

export function useUpdateEventRule() {
	const queryClient = useQueryClient();
	return useMutation<EventRule, Error, UpdateRuleArgs>({
		mutationFn: ({ ruleId, input }) => updateEventRule(ruleId, input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: EVENT_RULES_QUERY_KEY });
		},
	});
}

export function useDeleteEventRule() {
	const queryClient = useQueryClient();
	return useMutation<void, Error, number>({
		mutationFn: deleteEventRule,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: EVENT_RULES_QUERY_KEY });
		},
	});
}

export interface SetRuleImageArgs {
	ruleId: number;
	imageBase64: string;
	contentType: string;
}

export function useSetEventRuleImage() {
	const queryClient = useQueryClient();
	return useMutation<{ imageToken: string }, Error, SetRuleImageArgs>({
		mutationFn: ({ ruleId, imageBase64, contentType }) =>
			setEventRuleImage(ruleId, imageBase64, contentType),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: EVENT_RULES_QUERY_KEY });
		},
	});
}

export function useDeleteEventRuleImage() {
	const queryClient = useQueryClient();
	return useMutation<void, Error, number>({
		mutationFn: deleteEventRuleImage,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: EVENT_RULES_QUERY_KEY });
		},
	});
}

/** Test reguły: wyślij do canary (podgląd). Nie zmienia listy — bez invalidacji. */
export function useTestEventRuleCanary() {
	return useMutation<DispatchResult, Error, number>({
		mutationFn: testEventRuleCanary,
	});
}

/**
 * Incydenty reguły bloz — fetch dopiero po otwarciu drill-downu (`ruleId`
 * niech będzie null, dopóki dialog zamknięty). Ewaluator tyka co 15 min,
 * minutowy staleTime wystarcza.
 */
export function useBlozIncidentsQuery(ruleId: number | null, status: "active" | "all") {
	return useQuery<BlozIncident[]>({
		queryKey: ["device-agent", "bloz-incidents", ruleId, status],
		queryFn: () => listBlozIncidents(ruleId as number, status),
		enabled: ruleId != null,
		staleTime: 60_000,
	});
}

/** Aktywne zdarzenia urządzenia — ładowane gdy kafel jest rozwinięty (`enabled`). */
export function useDeviceEventIncidentsQuery(deviceUuid: string, enabled: boolean) {
	return useQuery<ActiveIncident[]>({
		queryKey: ["device-agent", "event-incidents", deviceUuid],
		queryFn: () => listDeviceEventIncidents(deviceUuid),
		enabled,
	});
}
